import { useMemo } from 'react'

import { useOpenWindow, useDebouncedValue } from '@krp/client-runtime'
import {
  COMMON_CATALOG_PAGE_SIZE,
  CommonCatalogScreen,
  PLATFORM_WINDOWS,
  catalogPageChosen,
  catalogSearchChanged,
  listResourceChosen,
  makeSelectCommonCatalogVm,
  platformCatalogProducts,
  type CommonCatalogActions,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { CONNECTION_FORMS } from '../../composition/connectionForms'
import { SourcesRows, useMyCatalogSources } from './SourcesStripBinding'
import { SEARCH_DEBOUNCE_MS } from './timings'
import { productPhotoUrl } from './useProductLinks'

const kindTitle = (kind: string) => CONNECTION_FORMS.titleOf(kind)
const selectIntent = (state: RootState) => state.platform.commonCatalog

/**
 * Binding «Номенклатура → Общий каталог» (UI-SECTIONS). Строка поиска уходит
 * запросом, когда человек перестал печатать (CLAUDE.md §7); пока идёт
 * следующая страница, на экране остаётся прежняя с пометкой «обновляется».
 * «Связи» открывают окно «Связи товаров» поверх каталога со списком ресурса этого товара; фото товара — на весь
 * экран, наименование — окно «Общий товар» (RP-FB50: общий каталог — эталон), «Создать товар» — окно нового товара.
 */
export function CommonCatalogBinding() {
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const intent = useAppSelector(selectIntent)
  const settled = useDebouncedValue(intent.query.trim(), SEARCH_DEBOUNCE_MS)
  const args = useMemo(
    () => ({ query: settled, page: intent.page, pageSize: COMMON_CATALOG_PAGE_SIZE }),
    [settled, intent.page],
  )
  const result = platformCatalogProducts.useQuery(args)
  const catalogSources = useMyCatalogSources()
  const selectVm = useMemo(() => makeSelectCommonCatalogVm(), [])
  const vm = selectVm({
    intent,
    // `data` хука — последняя полученная страница, `isFetching` — запрос в полёте (в смысле QueryResultLike).
    result: {
      ...(result.data !== undefined ? { data: result.data } : {}),
      ...(result.error !== undefined ? { error: result.error } : {}),
      isUninitialized: result.isUninitialized,
      isLoading: result.isFetching,
      isError: result.isError,
      isSuccess: result.isSuccess,
    },
    kindTitle,
  })

  const refetch = result.refetch
  const actions = useMemo<CommonCatalogActions>(
    () => ({
      searchChanged: (text) => dispatch(catalogSearchChanged(text)),
      pageChanged: (page) => dispatch(catalogPageChosen(page)),
      reload: () => void refetch(),
      // Поверх каталога: закрыли «Связи» — человек там же, где был, с тем же поиском и страницей.
      openLinks: (resourceId) => {
        if (resourceId !== null) dispatch(listResourceChosen(resourceId))
        openWindow(PLATFORM_WINDOWS.catalogLinks)
      },
      openProduct: (productId) => openWindow(PLATFORM_WINDOWS.commonProduct, { productId }),
      createProduct: () => openWindow(PLATFORM_WINDOWS.commonProductNew),
    }),
    [dispatch, openWindow, refetch],
  )
  return (
    <CommonCatalogScreen
      vm={vm}
      actions={actions}
      photoUrl={productPhotoUrl}
      sourceNames={catalogSources.map((resource) => resource.displayName)}
      sources={<SourcesRows resources={catalogSources} />}
    />
  )
}
